import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import './App.css';
import { 
  DocumentUpload, 
  StyleSelector, 
  ProcessingStage,
  AudioPlayer,
  Header,
  RecentDocuments,
  PricingSection,
  AuthModal,
  UpgradeModal
} from './components';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [currentPage, setCurrentPage] = useState('app'); // app, terms, privacy, pricing
  const [currentStage, setCurrentStage] = useState('upload'); // upload, style, processing, completed
  const [documentData, setDocumentData] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [scriptData, setScriptData] = useState(null);
  const [audioData, setAudioData] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [usageStatus, setUsageStatus] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // Initialize session and auth
  useEffect(() => {
    // Get or create session ID
    let storedSessionId = localStorage.getItem('commitly_session_id');
    if (!storedSessionId) {
      storedSessionId = 'session_' + Math.random().toString(36).substr(2, 16);
      localStorage.setItem('commitly_session_id', storedSessionId);
    }
    setSessionId(storedSessionId);

    // Check for stored auth token
    const storedToken = localStorage.getItem('commitly_token');
    if (storedToken) {
      setAuthToken(storedToken);
      // Validate token and get user profile
      validateToken(storedToken);
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 5000  // 5 second timeout
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        await fetchUsageStatus(token);
      } else {
        // Invalid token or server error
        console.warn('Token validation failed, clearing stored token');
        localStorage.removeItem('commitly_token');
        setAuthToken(null);
      }
    } catch (error) {
      console.warn('Backend unavailable for token validation, continuing in offline mode:', error);
      // Don't clear token if it's just a network error - might be temporary
      // Clear token and continue - user can re-authenticate when backend is available
      localStorage.removeItem('commitly_token');
      setAuthToken(null);
    }
  };

  const fetchUsageStatus = async (token = null) => {
    try {
      const headers = {};
      if (token || authToken) {
        headers['Authorization'] = `Bearer ${token || authToken}`;
      }

      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/auth/usage`);
      if (!user && sessionId) {
        url.searchParams.append('session_id', sessionId);
      }

      const response = await fetch(url, { 
        headers,
        timeout: 5000  // 5 second timeout
      });
      
      if (response.ok) {
        const usage = await response.json();
        setUsageStatus(usage);
        
        // Check if user needs upgrade
        if (usage.needs_upgrade) {
          if (user) {
            setShowUpgradeModal(true);
          } else {
            setShowAuthModal(true);
          }
        }
      } else {
        console.warn('Usage status fetch failed, continuing with default limits');
        // Set default usage status so app continues working
        setUsageStatus({
          podcast_count: 0,
          character_count: 0,
          podcasts_remaining: 2,
          characters_remaining: 3000,
          needs_upgrade: false
        });
      }
    } catch (error) {
      console.warn('Backend unavailable, using offline mode:', error);
      // Set default usage status for offline mode
      setUsageStatus({
        podcast_count: 0,
        character_count: 0,
        podcasts_remaining: 2,
        characters_remaining: 3000,
        needs_upgrade: false
      });
    }
  };

  const handleLogin = (userData, token) => {
    setUser(userData);
    setAuthToken(token);
    localStorage.setItem('commitly_token', token);
    setShowAuthModal(false);
    fetchUsageStatus(token);
  };

  const handleLogout = () => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('commitly_token');
    setUsageStatus(null);
    resetToUpload();
  };

  // Reset to upload stage
  const resetToUpload = () => {
    setCurrentPage('app');
    setCurrentStage('upload');
    setDocumentData(null);
    setSelectedStyle('');
    setScriptData(null);
    setAudioData(null);
    setProcessingStatus('');
  };

  // Navigate to different pages
  const navigateToPage = (page) => {
    setCurrentPage(page);
  };

  // Handle successful document upload
  const handleDocumentUploaded = (docData) => {
    setDocumentData(docData);
    setUsageStatus(docData.usage_status);
    setCurrentStage('style');
  };

  // Handle style selection
  const handleStyleSelected = (style) => {
    setSelectedStyle(style);
    setCurrentStage('processing');
  };

  // Handle script generation completion
  const handleScriptGenerated = (script) => {
    setScriptData(script);
    setUsageStatus(script.usage_status);
    setProcessingStatus('Script generated! Starting audio generation...');
  };

  // Handle audio generation completion
  const handleAudioGenerated = (audio) => {
    setAudioData(audio);
    setCurrentStage('completed');
    setProcessingStatus('Podcast generated successfully!');
  };

  // Handle usage limit reached
  const handleUsageLimitReached = (errorDetail) => {
    setUsageStatus(errorDetail.usage_status);
    
    if (user) {
      setShowUpgradeModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  const renderCurrentContent = () => {
    // Handle different pages first
    if (currentPage === 'terms') {
      return <TermsOfService onBack={() => setCurrentPage('app')} />;
    }
    
    if (currentPage === 'privacy') {
      return <PrivacyPolicy onBack={() => setCurrentPage('app')} />;
    }
    
    if (currentPage === 'pricing') {
      return <PricingSection onBack={() => setCurrentPage('app')} onUpgrade={() => setShowUpgradeModal(true)} />;
    }

    // Handle app stages
    switch(currentStage) {
      case 'upload':
        return (
          <div className="max-w-4xl mx-auto">
            <DocumentUpload 
              onDocumentUploaded={handleDocumentUploaded}
              sessionId={sessionId}
            />
            <RecentDocuments 
              onDocumentSelected={(doc) => {
                setDocumentData(doc);
                setCurrentStage('style');
              }}
            />
          </div>
        );
      
      case 'style':
        return (
          <div className="max-w-4xl mx-auto">
            <StyleSelector 
              documentData={documentData}
              onStyleSelected={handleStyleSelected}
              onBack={() => setCurrentStage('upload')}
            />
          </div>
        );
      
      case 'processing':
        return (
          <div className="max-w-4xl mx-auto">
            <ProcessingStage 
              documentData={documentData}
              selectedStyle={selectedStyle}
              onScriptGenerated={handleScriptGenerated}
              onAudioGenerated={handleAudioGenerated}
              processingStatus={processingStatus}
              onBack={() => setCurrentStage('style')}
              sessionId={sessionId}
            />
          </div>
        );
      
      case 'completed':
        return (
          <div className="max-w-6xl mx-auto">
            <AudioPlayer 
              scriptData={scriptData}
              audioData={audioData}
              documentData={documentData}
              selectedStyle={selectedStyle}
              onStartNew={resetToUpload}
            />
          </div>
        );
      
      default:
        return (
          <div className="max-w-4xl mx-auto">
            <DocumentUpload 
              onDocumentUploaded={handleDocumentUploaded}
              sessionId={sessionId}
            />
          </div>
        );
    }
  };

  return (
    <AuthContext.Provider value={{ user, authToken, usageStatus, handleLogin, handleLogout }}>
      <div className="App">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1f2937',
              color: '#f9fafb',
              border: '1px solid #374151'
            }
          }}
        />
        
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
          <Header 
            currentStage={currentStage} 
            onLogoClick={resetToUpload}
            documentData={documentData}
            user={user}
            onLogin={() => setShowAuthModal(true)}
            onLogout={handleLogout}
            usageStatus={usageStatus}
            onUpgrade={() => setShowUpgradeModal(true)}
          />
          
          <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
            {renderCurrentContent()}
          </main>

          {/* Authentication and Upgrade Modals */}
          <AuthModal 
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onLogin={handleLogin}
          />

          <UpgradeModal 
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            usageStatus={usageStatus}
            user={user}
            authToken={authToken}
          />

          {/* Footer */}
          <footer className="border-t border-gray-800 bg-black bg-opacity-50">
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="flex items-center space-x-4 mb-4 md:mb-0">
                  <h3 className="text-xl font-bold text-white">Commitly</h3>
                  <span className="text-gray-400">|</span>
                  <p className="text-gray-400">Transform documents into engaging podcasts</p>
                </div>
                
                <div className="flex space-x-6">
                  <button 
                    onClick={resetToUpload}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    New Podcast
                  </button>
                  <button
                    onClick={() => navigateToPage('pricing')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Pricing
                  </button>
                  {user && (
                    <button 
                      onClick={() => setShowUpgradeModal(true)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {user.subscription_status === 'pro' ? 'Pro Account' : 'Upgrade to Pro'}
                    </button>
                  )}
                  <button
                    onClick={() => navigateToPage('terms')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Terms
                  </button>
                  <button
                    onClick={() => navigateToPage('privacy')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Privacy
                  </button>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-800 text-center text-gray-500 text-sm">
                <p>&copy; 2025 Commitly. Powered by OpenAI GPT-4 and ElevenLabs.</p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
